import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts, P } from '../api';
import {
  Page, Section, Field, Select, Table, Td, Pill, Loading, ErrorNote, Empty, useLoad, useTS, when,
} from '../ui';

// Communications Center: review of office chats, customer texts and calls.
// Only the owner (and administrators the owner allows) can open it, and
// every view, search and export is written to the access log below.
const TABS = [['chats', 'Office chats'], ['sms', 'Customer texts'], ['calls', 'Calls & voicemail'], ['search', 'Search'], ['settings', 'Retention & access log']];

export default function TSComms() {
  const session = useTS();
  const [tab, setTab] = useState('chats');
  const staff = useLoad(() => ts.staff());
  const [memberId, setMemberId] = useState('');
  const people = [['', 'Everyone'], ...((staff.data || []).map(m => [m.id, m.full_name]))];
  return (
    <Page title="Communications Center" subtitle="Office chats, customer texts and calls. Every view is logged.">
      <div className="text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2" data-testid="ts-comms-notice">
        Staff were told when they activated Aster Phone that company chats, texts and calls can be reviewed by the owner and authorized administrators.
        Open this only for a business reason. Your views, searches and exports appear in the access log.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, label]) => (
          <Button key={k} size="sm" variant={tab === k ? 'default' : 'outline'} onClick={() => setTab(k)} data-testid={`ts-comms-tab-${k}`}>{label}</Button>
        ))}
        <div className="flex-1" />
        {tab !== 'settings' && <Select value={memberId} onChange={setMemberId} options={people} data-testid="ts-comms-member" />}
      </div>
      {tab === 'chats' && <Chats memberId={memberId} canExport={session.can(P.COMMS_EXPORT)} />}
      {tab === 'sms' && <Sms memberId={memberId} canExport={session.can(P.COMMS_EXPORT)} />}
      {tab === 'calls' && <Calls memberId={memberId} canExport={session.can(P.COMMS_EXPORT)} />}
      {tab === 'search' && <SearchTab memberId={memberId} />}
      {tab === 'settings' && <Settings owner={session.role === 'owner'} />}
    </Page>
  );
}

async function download(channel, memberId) {
  try {
    const blob = await ts.commsExport({ channel, member_id: memberId || undefined });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${channel}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error(e?.response?.status === 403 ? "You can't export communications" : (e.message || 'Export failed'));
  }
}

function ExportButton({ channel, memberId, show }) {
  if (!show) return null;
  return <Button size="sm" variant="outline" onClick={() => download(channel, memberId)} data-testid={`ts-comms-export-${channel}`}><Download className="w-4 h-4 mr-1" />Export CSV</Button>;
}

function Chats({ memberId, canExport }) {
  const list = useLoad(() => ts.commsChats({ member_id: memberId || undefined }), [memberId]);
  const [open, setOpen] = useState(null);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Section title="Chats" actions={<ExportButton channel="chat" memberId={memberId} show={canExport} />}>
        <ErrorNote error={list.error} />
        {list.loading ? <Loading /> : (list.data || []).length === 0 ? <Empty>No office chats yet.</Empty> : (
          <ul className="divide-y divide-border -mx-1">
            {list.data.map(c => (
              <li key={c.id}>
                <button className={`w-full text-left px-2 py-2 rounded-md hover:bg-muted ${open === c.id ? 'bg-muted' : ''}`}
                  onClick={() => setOpen(c.id)} data-testid={`ts-comms-chat-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{c.name}</span>
                    <Pill tone={c.kind === 'group' ? 'violet' : 'gray'}>{c.kind === 'group' ? 'Group' : '1:1'}</Pill>
                  </div>
                  <div className="text-xs text-muted-foreground">{c.messages} messages · {when(c.updated_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <div className="lg:col-span-2">{open ? <ChatView key={open} id={open} /> : <Section><Empty>Choose a chat to read it.</Empty></Section>}</div>
    </div>
  );
}

function Photo({ id }) {
  const [src, setSrc] = useState('');
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!shown) return undefined;
    let url = '';
    ts.commsImage(id).then(b => { url = URL.createObjectURL(b); setSrc(url); }).catch(() => setSrc(''));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, shown]);
  if (!shown) return <button className="text-xs underline text-primary" onClick={() => setShown(true)}>Show photo</button>;
  return src ? <img src={src} alt="Shared in chat" className="max-w-xs rounded-md border border-border" /> : <span className="text-xs text-muted-foreground">Loading photo…</span>;
}

function ChatView({ id }) {
  const { data, loading, error } = useLoad(() => ts.commsChat(id), [id]);
  if (loading) return <Section><Loading /></Section>;
  if (error) return <Section><ErrorNote error={error} /></Section>;
  return (
    <Section title={data.chat.name || 'Direct chat'}>
      {data.messages.length === 0 ? <Empty>No messages.</Empty> : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="ts-comms-chat-messages">
          {data.messages.map(m => (
            <div key={m.id} className={m.kind === 'system' ? 'text-xs text-center text-muted-foreground' : 'text-sm'}>
              {m.kind !== 'system' && <span className="font-medium">{m.sender_name || 'Former colleague'} </span>}
              <span className="text-xs text-muted-foreground">{when(m.created_at)}</span>
              {m.deleted_at ? <div className="italic text-muted-foreground">Message deleted</div> : (
                <>
                  {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                  {m.attachment_id && <Photo id={m.attachment_id} />}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function Sms({ memberId, canExport }) {
  const list = useLoad(() => ts.commsSms({ member_id: memberId || undefined }), [memberId]);
  const [open, setOpen] = useState(null);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Section title="Conversations" actions={<ExportButton channel="sms" memberId={memberId} show={canExport} />}>
        <ErrorNote error={list.error} />
        {list.loading ? <Loading /> : (list.data || []).length === 0 ? <Empty>No customer texts yet.</Empty> : (
          <ul className="divide-y divide-border -mx-1">
            {list.data.map(c => (
              <li key={c.id}>
                <button className={`w-full text-left px-2 py-2 rounded-md hover:bg-muted ${open === c.id ? 'bg-muted' : ''}`} onClick={() => setOpen(c.id)}>
                  <div className="text-sm font-medium">{c.external || c.customer_number}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.last_message || ''}</div>
                  <div className="text-xs text-muted-foreground">{when(c.last_message_at)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <div className="lg:col-span-2">{open ? <SmsView key={open} id={open} /> : <Section><Empty>Choose a conversation.</Empty></Section>}</div>
    </div>
  );
}

function SmsView({ id }) {
  const { data, loading, error } = useLoad(() => ts.commsSmsThread(id), [id]);
  if (loading) return <Section><Loading /></Section>;
  if (error) return <Section><ErrorNote error={error} /></Section>;
  return (
    <Section title={data.conversation.external || 'Conversation'}>
      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {data.messages.map(m => (
          <div key={m.id} className={`text-sm max-w-[80%] rounded-lg px-3 py-2 ${m.direction === 'outbound' ? 'ml-auto bg-violet-500/10' : 'bg-muted'}`}>
            <div className="whitespace-pre-wrap">{m.body}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{m.member_name ? `${m.member_name} · ` : ''}{when(m.created_at)} · {m.status}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Calls({ memberId, canExport }) {
  const [kind, setKind] = useState('');
  const list = useLoad(() => ts.commsCalls({ member_id: memberId || undefined, kind: kind || undefined }), [memberId, kind]);
  return (
    <Section title="Calls" actions={(
      <div className="flex gap-2">
        <Select value={kind} onChange={setKind} options={[['', 'All calls'], ['missed', 'Missed'], ['voicemail', 'Voicemail'], ['recorded', 'Recorded']]} data-testid="ts-comms-call-kind" />
        <ExportButton channel="calls" memberId={memberId} show={canExport} />
      </div>
    )}>
      <ErrorNote error={list.error} />
      {list.loading ? <Loading /> : (list.data || []).length === 0 ? <Empty>No calls.</Empty> : (
        <Table head={['When', 'Who', 'Customer', 'Direction', 'Status', 'Length', 'Recording / summary']}>
          {list.data.map(c => (
            <tr key={c.id}>
              <Td>{when(c.created_at)}</Td>
              <Td>{c.member_name || '—'}</Td>
              <Td>{c.customer_number}</Td>
              <Td>{c.direction}{c.via === 'app' ? <span className="text-xs text-muted-foreground"> · app</span> : null}</Td>
              <Td>{c.missed ? <Pill tone="amber">Missed</Pill> : c.status}</Td>
              <Td>{c.talk_seconds || c.duration_seconds || 0}s</Td>
              <Td className="text-xs max-w-xs">
                {c.voicemail_url && <a className="underline text-primary mr-2" href={c.voicemail_url} target="_blank" rel="noreferrer">Voicemail</a>}
                {c.recording_url && <a className="underline text-primary mr-2" href={c.recording_url} target="_blank" rel="noreferrer">Recording</a>}
                {c.summary && <div className="text-muted-foreground">{c.summary}</div>}
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function SearchTab({ memberId }) {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { setRes(await ts.commsSearch({ q, member_id: memberId || undefined, date_from: from || undefined, date_to: to || undefined })); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Section title="Search chats, texts and calls">
      <form onSubmit={run} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="ts-comms-search-form">
        <Field label="Words or a phone number"><Input value={q} onChange={e => setQ(e.target.value)} data-testid="ts-comms-search-q" /></Field>
        <Field label="From"><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></Field>
        <Button type="submit" disabled={busy || q.trim().length < 2} data-testid="ts-comms-search-go"><Search className="w-4 h-4 mr-1" />Search</Button>
      </form>
      <ErrorNote error={error} />
      {res && (
        <div className="space-y-4" data-testid="ts-comms-search-results">
          <ResultList title={`Office chats (${res.chat.length})`} rows={res.chat}
            render={m => <><span className="font-medium">{m.sender_name}</span> <span className="text-xs text-muted-foreground">{when(m.created_at)}</span><div>{m.text}</div></>} />
          <ResultList title={`Customer texts (${res.sms.length})`} rows={res.sms}
            render={m => <><span className="font-medium">{m.external}</span> <span className="text-xs text-muted-foreground">{when(m.created_at)} · {m.direction}</span><div>{m.body}</div></>} />
          <ResultList title={`Calls (${res.calls.length})`} rows={res.calls}
            render={c => <><span className="font-medium">{c.customer_number}</span> <span className="text-xs text-muted-foreground">{when(c.created_at)} · {c.member_name}</span>{c.summary && <div>{c.summary}</div>}</>} />
        </div>
      )}
    </Section>
  );
}

function ResultList({ title, rows, render }) {
  return (
    <div>
      <div className="text-xs font-medium mb-1">{title}</div>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground">Nothing found.</p> : (
        <ul className="space-y-2 text-sm">{rows.map(r => <li key={r.id} className="border-l-2 border-border pl-2">{render(r)}</li>)}</ul>
      )}
    </div>
  );
}

const RETENTION_LABEL = { null: 'Keep until I delete', 90: '90 days', 365: '1 year', 1095: '3 years' };

function Settings({ owner }) {
  const ret = useLoad(() => ts.commsRetention());
  const log = useLoad(() => ts.commsAccessLog());
  const save = async (v) => {
    try { await ts.setCommsRetention(v === 'null' ? null : Number(v)); toast.success('Retention saved'); ret.reload(); log.reload(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <>
      <Section title="How long office chats are kept">
        {ret.loading ? <Loading /> : ret.error ? <ErrorNote error={ret.error} /> : (
          <div className="flex flex-wrap items-center gap-3">
            {owner ? (
              <Select value={String(ret.data.chat_days)} onChange={save} data-testid="ts-comms-retention"
                options={ret.data.choices.map(c => [String(c), RETENTION_LABEL[c]])} />
            ) : <span className="text-sm">{RETENTION_LABEL[ret.data.chat_days]}</span>}
            <span className="text-xs text-muted-foreground">Older chat messages and photos are deleted every night. Call and text records follow your company record-keeping.</span>
          </div>
        )}
      </Section>
      <Section title="Access log">
        {log.loading ? <Loading /> : log.error ? <ErrorNote error={log.error} /> : (log.data || []).length === 0 ? <Empty>No access yet.</Empty> : (
          <Table head={['When', 'Who', 'What']}>
            {log.data.map((a, i) => (
              <tr key={a.id || i} data-testid="ts-comms-log-row">
                <Td>{when(a.created_at)}</Td>
                <Td>{a.member_name || a.actor_name || '—'}</Td>
                <Td className="text-xs">{(a.action || '').replace('comms.', '').replace(/_/g, ' ')}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}
